import React, { useState, useRef, useEffect } from 'react';
import { X } from 'lucide-react';
import { Button } from './button';

/**
 * WheelTimePicker - A scroll wheel time picker component
 * Similar to iOS/Android native time pickers
 */
export function WheelTimePicker({ 
  availableSlots = [], 
  value, 
  onChange, 
  onClose,
  isOpen = false
}) {
  // Parse available slots to get unique hours and minutes
  const availableHours = [...new Set(availableSlots.map(slot => slot.split(':')[0]))].sort();
  const [selectedHour, setSelectedHour] = useState(value?.split(':')[0] || availableHours[0] || '09');
  const [selectedMinute, setSelectedMinute] = useState(value?.split(':')[1] || '00');
  
  // Get available minutes for selected hour
  const availableMinutes = availableSlots
    .filter(slot => slot.startsWith(selectedHour + ':'))
    .map(slot => slot.split(':')[1])
    .sort();

  const hourRef = useRef(null);
  const minuteRef = useRef(null);

  // Update selected minute when hour changes
  useEffect(() => {
    if (availableMinutes.length > 0 && !availableMinutes.includes(selectedMinute)) {
      setSelectedMinute(availableMinutes[0]);
    }
  }, [selectedHour, availableMinutes, selectedMinute]);

  // Scroll to selected item on open
  useEffect(() => {
    if (isOpen) {
      setTimeout(() => {
        scrollToSelected(hourRef, selectedHour, availableHours);
        scrollToSelected(minuteRef, selectedMinute, availableMinutes);
      }, 100);
    }
  }, [isOpen, selectedHour, selectedMinute, availableHours, availableMinutes]);

  const scrollToSelected = (ref, value, items) => {
    if (ref.current) {
      const index = items.indexOf(value);
      if (index !== -1) {
        const itemHeight = 48;
        ref.current.scrollTop = index * itemHeight;
      }
    }
  };

  const handleScroll = (ref, items, setter) => {
    if (ref.current) {
      const itemHeight = 48;
      const scrollTop = ref.current.scrollTop;
      const index = Math.round(scrollTop / itemHeight);
      const clampedIndex = Math.min(Math.max(0, index), items.length - 1);
      if (items[clampedIndex]) {
        setter(items[clampedIndex]);
      }
    }
  };

  const handleConfirm = () => {
    const time = `${selectedHour}:${selectedMinute}`;
    if (availableSlots.includes(time)) {
      onChange(time);
      onClose();
    }
  };

  const currentTime = `${selectedHour}:${selectedMinute}`;
  const isValidTime = availableSlots.includes(currentTime);

  if (!isOpen) return null;

  return (
    <>
      {/* Backdrop */}
      <div 
        className="fixed inset-0 bg-black/50 z-50 animate-fade-in"
        onClick={onClose}
      />
      
      {/* Modal */}
      <div className="fixed bottom-0 left-0 right-0 bg-background rounded-t-3xl z-50 animate-slide-up shadow-2xl">
        {/* Handle */}
        <div className="flex justify-center py-3">
          <div className="w-12 h-1.5 bg-muted-foreground/30 rounded-full" />
        </div>
        
        {/* Header */}
        <div className="flex items-center justify-between px-6 pb-4">
          <h3 className="text-lg font-semibold">Scegli orario</h3>
          <button 
            onClick={onClose}
            className="p-2 hover:bg-muted rounded-full transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>
        
        {/* Time Picker Wheels */}
        <div className="relative px-6 pb-4">
          {/* Selection indicator */}
          <div className="absolute left-6 right-6 top-1/2 -translate-y-1/2 h-12 bg-muted/50 rounded-xl pointer-events-none z-0" />
          
          <div className="flex gap-4 relative z-10">
            {/* Hours Column */}
            <div className="flex-1 relative">
              <div 
                ref={hourRef}
                className="h-[192px] overflow-y-auto scroll-smooth hide-scrollbar snap-y snap-mandatory"
                onScroll={() => handleScroll(hourRef, availableHours, setSelectedHour)}
                style={{ scrollSnapType: 'y mandatory' }}
              >
                {/* Padding items */}
                <div className="h-[72px]" />
                
                {availableHours.map((hour) => (
                  <div
                    key={hour}
                    className={`h-12 flex items-center justify-center text-2xl font-semibold transition-all snap-center cursor-pointer ${
                      hour === selectedHour 
                        ? 'text-foreground scale-110' 
                        : 'text-muted-foreground/50 scale-90'
                    }`}
                    onClick={() => {
                      setSelectedHour(hour);
                      scrollToSelected(hourRef, hour, availableHours);
                    }}
                  >
                    {hour}
                  </div>
                ))}
                
                {/* Padding items */}
                <div className="h-[72px]" />
              </div>
            </div>
            
            {/* Separator */}
            <div className="flex items-center">
              <span className="text-2xl font-bold text-muted-foreground">:</span>
            </div>
            
            {/* Minutes Column */}
            <div className="flex-1 relative">
              <div 
                ref={minuteRef}
                className="h-[192px] overflow-y-auto scroll-smooth hide-scrollbar snap-y snap-mandatory"
                onScroll={() => handleScroll(minuteRef, availableMinutes, setSelectedMinute)}
                style={{ scrollSnapType: 'y mandatory' }}
              >
                {/* Padding items */}
                <div className="h-[72px]" />
                
                {availableMinutes.map((minute) => (
                  <div
                    key={minute}
                    className={`h-12 flex items-center justify-center text-2xl font-semibold transition-all snap-center cursor-pointer ${
                      minute === selectedMinute 
                        ? 'text-foreground scale-110' 
                        : 'text-muted-foreground/50 scale-90'
                    }`}
                    onClick={() => {
                      setSelectedMinute(minute);
                      scrollToSelected(minuteRef, minute, availableMinutes);
                    }}
                  >
                    {minute}
                  </div>
                ))}
                
                {/* Padding items */}
                <div className="h-[72px]" />
              </div>
            </div>
          </div>
        </div>
        
        {/* Confirm Button */}
        <div className="px-6 pb-8">
          <Button 
            className="w-full h-14 text-lg font-semibold"
            onClick={handleConfirm}
            disabled={!isValidTime}
            data-testid="confirm-time-btn"
          >
            Seleziona {currentTime}
          </Button>
        </div>
      </div>
      
      <style jsx>{`
        .hide-scrollbar {
          -ms-overflow-style: none;
          scrollbar-width: none;
        }
        .hide-scrollbar::-webkit-scrollbar {
          display: none;
        }
        @keyframes slide-up {
          from {
            transform: translateY(100%);
          }
          to {
            transform: translateY(0);
          }
        }
        .animate-slide-up {
          animation: slide-up 0.3s ease-out;
        }
        @keyframes fade-in {
          from {
            opacity: 0;
          }
          to {
            opacity: 1;
          }
        }
        .animate-fade-in {
          animation: fade-in 0.2s ease-out;
        }
      `}</style>
    </>
  );
}

/**
 * TimePickerTrigger - Button that opens the wheel time picker
 */
export function TimePickerTrigger({
  availableSlots = [],
  value,
  onChange,
  placeholder = "Seleziona orario",
  disabled = false
}) {
  const [isOpen, setIsOpen] = useState(false);

  if (availableSlots.length === 0) {
    return (
      <div className="text-center py-8 text-muted-foreground">
        <div className="w-12 h-12 mx-auto mb-3 rounded-full bg-muted flex items-center justify-center">
          <span className="text-2xl">🕐</span>
        </div>
        <p>Nessun orario disponibile</p>
      </div>
    );
  }

  return (
    <>
      <button
        type="button"
        onClick={() => !disabled && setIsOpen(true)}
        disabled={disabled}
        className={`w-full h-14 px-4 rounded-xl border-2 transition-all flex items-center justify-between ${
          value 
            ? 'border-primary bg-primary/5' 
            : 'border-border hover:border-primary/50'
        } ${disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
        data-testid="time-picker-trigger"
      >
        <span className={`text-lg ${value ? 'font-semibold' : 'text-muted-foreground'}`}>
          {value || placeholder}
        </span>
        <div className="flex items-center gap-2 text-muted-foreground">
          <span className="text-sm">{availableSlots.length} orari</span>
          <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
        </div>
      </button>

      <WheelTimePicker
        availableSlots={availableSlots}
        value={value}
        onChange={onChange}
        onClose={() => setIsOpen(false)}
        isOpen={isOpen}
      />
    </>
  );
}

export default TimePickerTrigger;
